/**
 * Product (mahsulot) Zod schemalar
 *
 * Kanonik qarorlar:
 *   - Default priceMultiplier: 25 (25% markup)
 *   - Default status: MAVJUD
 *   - Default currency: UZS
 *   - Default stock: 0
 *   - Default minStock: 5
 */
import { z } from 'zod';
import { uuidSchema } from './common.schema.js';
// ─── Enums (types/product.types.ts bilan mos) ─────────────────────
export const productStatusEnum = z.enum(['MAVJUD', 'KUTILMOQDA', 'TUGAGAN']);
export const currencyEnum = z.enum(['UZS', 'USD', 'RUB', 'CNY']);
// ─── Product variant (Faza C — kengaytirilgan) ────────────────────
export const productVariantSchema = z.object({
    // Eski (name majburiy, value optional — foydalanuvchi xohishi)
    name: z.string().min(1, 'Variant nomi kerak').max(200),
    value: z.string().max(200).optional().default(''),
    priceModifier: z.number().min(-1_000_000_000).max(1_000_000_000).default(0),
    stockModifier: z.number().int().min(-1_000_000).max(1_000_000).default(0),
    // Yangi (Faza C — optional, to'liq variant qiymati)
    sku: z.string().max(50).optional(),
    customId: z.string().max(100).optional(),
    basePrice: z.number().min(0).max(1_000_000_000).optional(),
    priceMultiplier: z.number().min(0).max(1000).optional(),
    price: z.number().min(0).max(1_000_000_000).optional(),
    currency: currencyEnum.optional(),
    stock: z.number().int().min(0).max(1_000_000).optional(),
    minStock: z.number().int().min(0).max(1_000_000).optional(),
    status: productStatusEnum.optional(),
    categoryId: uuidSchema.optional(),
    images: z.array(z.string().min(1).max(500)).max(10).optional(),
    // POS-Variant (2026-05-21) — qo'shimcha skaner identifikatorlari
    // customId yuqorida — eski tizimdan; quyidagilar yangi
    barcodeId: z.string().max(100).optional(), // alohida EAN/QR shtrix-kod
    code: z.string().max(100).optional(), // Excel/ishlab chiqaruvchi kod
    catalogNumber: z.string().max(100).optional(), // OEM/parts katalog №
});
// ─── Create product ───────────────────────────────────────────────
export const createProductSchema = z.object({
    // 300: avto-qism nomlari uzun + Kiril→Lotin transliteratsiya ~40% kengaytiradi
    // (ш→sh, ю→yu, ч→ch). 200 ba'zi nomlarda saqlashda xato berardi.
    name: z.string().min(1, 'Nomi kiritilmagan').max(300),
    sku: z.string().max(50).optional(),
    /** Mahsulot kodi (ishlab chiqaruvchi / ichki kod, masalan: 34485) */
    code: z.string().max(100).optional(),
    /** OEM / parts katalog № (masalan: AZ16424430079) */
    catalogNumber: z.string().max(100).optional(),
    barcode: z.string().max(50).optional(),
    description: z.string().max(2000).optional(),
    // Narx (kanonik markup default 25%)
    basePrice: z.number().min(0, "Asl narx manfiy bo'lmasligi kerak").max(1_000_000_000),
    priceMultiplier: z
        .number()
        .min(0, "Foiz manfiy bo'lmasligi kerak")
        .max(1000, 'Foiz juda katta')
        .default(25),
    currency: currencyEnum.default('UZS'),
    // Stok
    stock: z.number().int().min(0).max(1_000_000).default(0),
    minStock: z.number().int().min(0).max(1_000_000).default(5),
    unit: z.string().max(20).optional(),
    // Status
    status: productStatusEnum.default('MAVJUD'),
    // Aloqalar
    categoryId: uuidSchema.optional(),
    storeId: uuidSchema.optional(),
    // Rasmlar — Faza B'da disk upload qo'shiladi.
    // Hozircha URL formatda (eski URL'lar) yoki disk-relative path (Faza B):
    //   - "https://example.com/image.jpg"
    //   - "/uploads/products/<storeId>/<productId>/<uuid>.webp"
    // Birinchi rasm asosiy hisoblanadi (Prisma `image`), qolgani `images` ga yoziladi.
    images: z.array(z.string().min(1).max(500)).max(20).optional(),
    // Variantlar
    variants: z.array(productVariantSchema).max(50).optional(),
    // OEM va cross-reference (Faza A)
    oemNumber: z.string().max(100).optional(),
    videoUrl: z.string().url("Video URL formati noto'g'ri").max(500).optional(),
    crossRefNumbers: z.array(z.string().min(1).max(100)).max(50).optional(),
    // Parent-child auto-activate (Faza F)
    parentProductId: uuidSchema.optional(),
    childProductIds: z.array(uuidSchema).max(50).optional(),
    isHidden: z.boolean().optional(),
    autoActivateChildren: z.boolean().optional(),
    // Import audit (Faza G — Excel)
    importSource: z.string().max(255).optional(),
    importOrder: z.number().int().min(1).optional(),
});
// ─── Update product (barcha fieldlar optional) ────────────────────
export const updateProductSchema = createProductSchema.partial();
// ─── Query (ro'yxat olish) ────────────────────────────────────────
export const productQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    search: z.string().max(100).optional(),
    categoryId: uuidSchema.optional(),
    status: productStatusEnum.optional(),
    storeId: uuidSchema.optional(),
    // Filter: stockdagi
    minStock: z.coerce.number().int().min(0).optional(),
    maxStock: z.coerce.number().int().min(0).optional(),
    // Filter: shtrix kod ulanganmi ('true' = barcode bor, 'false' = yo'q).
    // Query string'dan keladi — Boolean("false")=true tuzoqdan qochish uchun manual.
    hasBarcode: z
        .union([z.boolean(), z.string()])
        .transform((v) => {
        if (typeof v === 'boolean')
            return v;
        const s = v.toLowerCase().trim();
        return s === 'true' || s === '1';
    })
        .optional(),
    // Sort — 'sku' raqamli (skuSort ustuni orqali), default katalog tartibi
    sortBy: z.enum(['name', 'price', 'stock', 'createdAt', 'soldCount', 'sku']).default('sku'),
    sortOrder: z.enum(['asc', 'desc']).default('asc'),
    // Keyset pagination — oxirgi element kursori (<sortValue>|id ICHKI base64).
    // Berilganda OFFSET emas, keyset ishlatiladi (10k+ da O(1)).
    cursor: z.string().max(200).optional(),
});
// ─── Restock (qabul qilish) ───────────────────────────────────────
export const restockProductSchema = z.object({
    productId: uuidSchema,
    quantity: z.number().int().min(1, "Miqdor 1 dan kam bo'lmasligi kerak"),
    newBasePrice: z.number().min(0).max(1_000_000_000).optional(),
    reason: z.string().max(500).optional(),
});
// ─── Bulk price update ────────────────────────────────────────────
export const bulkPriceUpdateSchema = z.object({
    productIds: z.array(uuidSchema).min(1).max(1000),
    changeType: z.enum(['percent', 'fixed']),
    changeValue: z.number(),
    applyToBasePrice: z.boolean().default(false),
});
// ─── SKU range (bulk amallar uchun — spec bo'lim 7) ───────────────
// SKU lar numeric ham, alphanumeric ham bo'lishi mumkin — lekin "oralig'i"
// faqat sof numeric SKU'lar uchun mantiqli. Backend bu filtr bilan ishlaydi.
const skuRangeFields = {
    minSku: z.coerce.number().int().min(0).max(1_000_000_000),
    maxSku: z.coerce.number().int().min(0).max(1_000_000_000),
};
// ─── Bulk category update (spec 7 — "Kategoriya o'zgartirish") ────
// Ikki rejim (bulkDeleteSchema bilan bir xil pattern):
//   - 'ids':   jadvalda tanlangan mahsulotlar (HAR QANDAY SKU — null,
//              alfanumerik ham ishlaydi). Asosiy professional rejim.
//   - 'range': sof numeric SKU oralig'i (eski xulq, ixtiyoriy)
const bulkCategoryTargetFields = {
    categoryId: uuidSchema,
    priceMultiplier: z.number().min(0).max(1000).optional(),
};
export const bulkCategoryUpdateSchema = z
    .discriminatedUnion('mode', [
    z.object({
        mode: z.literal('ids'),
        productIds: z.array(uuidSchema).min(1).max(1000),
        ...bulkCategoryTargetFields,
    }),
    z.object({
        mode: z.literal('range'),
        ...skuRangeFields,
        ...bulkCategoryTargetFields,
    }),
])
    .superRefine((data, ctx) => {
    if (data.mode === 'range' && data.minSku > data.maxSku) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min SKU Max SKU dan katta bo'lmasligi kerak",
            path: ['maxSku'],
        });
    }
});
// ─── Bulk delete (spec 7 — "Barcha mahsulotlarni o'chirish") ──────
// Ikki rejim:
//   - 'all': butunlay tozalash (QAYTARIB BO'LMAYDI)
//   - 'range': SKU oralig'i bo'yicha
//
// Discriminated union .refine() bilan ishlamaydi — minSku ≤ maxSku
// validation discriminated union ustidan superRefine bilan qo'llanadi.
export const bulkDeleteSchema = z
    .discriminatedUnion('mode', [
    z.object({
        mode: z.literal('all'),
        confirmPhrase: z.literal('HAMMASINI OCHIR', {
            errorMap: () => ({ message: "Tasdiq iborasi noto'g'ri" }),
        }),
    }),
    z.object({
        mode: z.literal('range'),
        ...skuRangeFields,
    }),
])
    .superRefine((data, ctx) => {
    if (data.mode === 'range' && data.minSku > data.maxSku) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min SKU Max SKU dan katta bo'lmasligi kerak",
            path: ['maxSku'],
        });
    }
});
// ─── Empty codes query (spec 7 — "Bo'sh kodlar topish") ───────────
export const emptyCodesQuerySchema = z.object({
    from: z.coerce.number().int().min(0).max(1_000_000_000).default(1),
    to: z.coerce.number().int().min(1).max(1_000_000_000).default(1000),
    limit: z.coerce.number().int().min(1).max(10_000).default(500),
});
// ─── SKU range query (spec 7 — bulk label, batch operatsiyalar uchun) ──
export const skuRangeQuerySchema = z
    .object({
    minSku: z.coerce.number().int().min(0).max(1_000_000_000),
    maxSku: z.coerce.number().int().min(0).max(1_000_000_000),
})
    .refine((data) => data.minSku <= data.maxSku, {
    message: "Min SKU Max SKU dan katta bo'lmasligi kerak",
    path: ['maxSku'],
});
// ─── SKU ro'yxati (bulk etiketka — aniq kodlar ro'yxati uchun) ────
// Foydalanuvchi uzluksiz oraliq emas, aralash SKU kodlarni (masalan
// 1529, 632, 353) qo'yib etiketka chop etadi. POST body — ro'yxat
// uzun bo'lishi mumkin (query string'ga sig'maydi).
export const skuListSchema = z.object({
    skus: z
        .array(z.string().trim().min(1).max(64))
        .min(1, "Kamida bitta SKU kerak")
        .max(10000, "Bir martada ko'pi bilan 10000 ta SKU"),
});
// ─── Product history (spec 7 — "Tarix") ───────────────────────────
// ProductHistoryAction type — types/product.types.ts da
export const productHistoryActionEnum = z.enum([
    'CREATED',
    'UPDATED',
    'DELETED',
    'STOCK_CHANGED',
    'PRICE_CHANGED',
    'VARIANT_ADDED',
    'VARIANT_UPDATED',
    // Excel import audit — eski AvtoFix-main rejimi (autoGroup)
    'CREATED_VIA_EXCEL',
    'VARIANT_ADDED_VIA_EXCEL',
]);
export const productHistoryQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    scope: z.enum(['today', 'past', 'all']).default('all'),
    productId: uuidSchema.optional(),
});
//# sourceMappingURL=product.schema.js.map